<template>
    <div class="calendar-preview-widget">
        <div class="calendar-preview-header">
            <div class="calendar-preview-header-left">
                <div class="calendar-info" v-if="calendar_item && parsedEventData">
                    <h3 class="calendar-title">{{ parsedEventData.summary || 'No Title' }}</h3>
                    <p class="calendar-meta">
                        <strong>Organizer:</strong> {{ parsedEventData.organizer?.email || calendar_item.from }}
                    </p>
                    <p class="calendar-meta"
                        v-if="parsedEventData.creator && parsedEventData.creator.email !== parsedEventData.organizer?.email">
                        <strong>Created by:</strong> {{ parsedEventData.creator.email }}
                    </p>
                    <p class="calendar-meta">
                        <strong>Start:</strong> {{ formatEventDateTime(parsedEventData.start) }}
                    </p>
                    <p class="calendar-meta">
                        <strong>End:</strong> {{ formatEventDateTime(parsedEventData.end) }}
                    </p>
                    <p class="calendar-meta" v-if="parsedEventData.location">
                        <strong>Location:</strong> {{ parsedEventData.location }}
                    </p>
                    <p class="calendar-meta">
                        <strong>Status:</strong> {{ parsedEventData.status || 'Unknown' }}
                    </p>
                </div>
            </div>
            <div class="calendar-preview-options" v-if="calendar_item">
                <a v-if="parsedEventData && parsedEventData.htmlLink" :href="parsedEventData.htmlLink" target="_blank"
                    class="btn-calendar-link">
                    Open in Calendar
                </a>
            </div>
        </div>

        <div class="calendar-preview-body">
            <div class="event-details">
                <div class="event-section" v-if="parsedEventData && parsedEventData.description">
                    <h4>Description</h4>
                    <div class="event-description" v-html="formatDescription(parsedEventData.description)"></div>
                </div>

                <div class="event-section"
                    v-if="parsedEventData && parsedEventData.attendees && parsedEventData.attendees.length > 0">
                    <h4>Attendees</h4>
                    <ul class="attendees-list">
                        <li v-for="(attendee, index) in parsedEventData.attendees" :key="index" class="attendee-item">
                            <span class="attendee-email">{{ attendee.email }}</span>
                            <span class="attendee-status"
                                :class="'status-' + (attendee.responseStatus || 'needsAction')">
                                {{ formatResponseStatus(attendee.responseStatus) }}
                            </span>
                        </li>
                    </ul>
                </div>

                <div class="event-section" v-if="parsedEventData && parsedEventData.recurrence">
                    <h4>Recurrence</h4>
                    <ul class="recurrence-list">
                        <li v-for="(rule, index) in parsedEventData.recurrence" :key="index">
                            {{ rule }}
                        </li>
                    </ul>
                </div>

                <div class="event-section" v-if="parsedEventData && parsedEventData.reminders">
                    <h4>Reminders</h4>
                    <p v-if="parsedEventData.reminders.useDefault">Using default reminders</p>
                    <ul v-else-if="parsedEventData.reminders.overrides" class="reminders-list">
                        <li v-for="(reminder, index) in parsedEventData.reminders.overrides" :key="index">
                            {{ reminder.method }}: {{ reminder.minutes }} minutes before
                        </li>
                    </ul>
                </div>

                <div class="event-section"
                    v-if="!parsedEventData || (!parsedEventData.description && (!parsedEventData.attendees || parsedEventData.attendees.length === 0))">
                    <p class="no-additional-details">No additional event details available.</p>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
export default {
    name: "CalendarPreviewWidget",
    props: {
        calendar_item: {
            type: Object,
            default: null,
        },
    },
    computed: {
        parsedEventData() {
            if (!this.calendar_item || !this.calendar_item.action) {
                return null;
            }

            try {
                return JSON.parse(this.calendar_item.action);
            } catch (error) {
                console.error('Error parsing calendar event data:', error);
                return null;
            }
        }
    },
    methods: {
        formatEventDateTime(dateTimeObj) {
            if (!dateTimeObj) return 'Unknown time';

            try {
                let dateToFormat;

                if (dateTimeObj.dateTime) {
                    // DateTime with timezone
                    dateToFormat = new Date(dateTimeObj.dateTime);
                } else if (dateTimeObj.date) {
                    // All-day event
                    dateToFormat = new Date(dateTimeObj.date);
                    return dateToFormat.toLocaleDateString() + ' (All day)';
                } else {
                    return 'Unknown time';
                }

                const options = {
                    year: 'numeric',
                    month: 'short',
                    day: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit',
                    timeZoneName: 'short'
                };

                return dateToFormat.toLocaleString(undefined, options);
            } catch (error) {
                console.error('Error formatting date:', error);
                return 'Invalid date';
            }
        },
        formatDescription(description) {
            if (!description) return '';

            // Check if description looks like HTML
            if (description.includes('<') || description.includes('&')) {
                return description;
            } else {
                // Plain text - convert newlines to <br>
                return description.replace(/\r\n/g, '<br>').replace(/\n/g, '<br>');
            }
        },
        formatResponseStatus(status) {
            const statusMap = {
                'needsAction': 'Needs Action',
                'declined': 'Declined',
                'tentative': 'Maybe',
                'accepted': 'Accepted'
            };
            return statusMap[status] || status || 'Unknown';
        }
    },
};
</script>

<style lang="scss" scoped>
.calendar-preview-widget {
    width: 100%;
    height: 100%;
    background-color: #fff;
    border-radius: 5px;
    display: flex;
    flex-direction: column;
    padding: 15px;

    .calendar-preview-header {
        display: flex;
        align-items: flex-start;
        justify-content: space-between;
        padding-bottom: 15px;
        border-bottom: 1px solid #eee;
        margin-bottom: 15px;

        .calendar-preview-header-left {
            display: flex;
            align-items: flex-start;
            flex: 1;

            .calendar-info {
                display: flex;
                flex-direction: column;

                .calendar-title {
                    font-family: "Nunito", sans-serif;
                    font-size: 18px;
                    font-weight: 700;
                    color: #1c1c1c;
                    margin: 0 0 8px 0;
                }

                .calendar-meta {
                    font-family: "Nunito", sans-serif;
                    font-size: 11px;
                    color: #555;
                    margin: 2px 0;

                    strong {
                        font-weight: 600;
                    }
                }
            }
        }

        .calendar-preview-options {
            display: flex;
            align-items: center;
            margin-left: auto;
            padding-left: 15px;

            .btn-calendar-link {
                background: #0884AF;
                color: #fff;
                height: 30px;
                border-radius: 5px;
                border: none;
                font-style: bold !important;
                font-weight: 500;
                width: -moz-max-content;
                width: max-content;
                position: relative;
                cursor: pointer;
                display: flex;
                align-items: center;
                justify-content: center;
                padding: 0px 14px;
                transition: all 0.2s ease-in-out;
                text-decoration: none;
            }
        }
    }

    .calendar-preview-body {
        flex-grow: 1;
        overflow-y: auto;

        .event-details {
            .event-section {
                margin-bottom: 20px;

                h4 {
                    font-family: "Nunito", sans-serif;
                    font-size: 14px;
                    font-weight: 600;
                    color: #333;
                    margin: 0 0 8px 0;
                }

                .event-description {
                    font-family: "Nunito", sans-serif;
                    font-size: 13px;
                    line-height: 1.5;
                    color: #444;
                    background: #f9f9f9;
                    padding: 10px;
                    border-radius: 5px;
                    border-left: 3px solid #1a73e8;
                }

                .attendees-list,
                .recurrence-list,
                .reminders-list {
                    list-style: none;
                    padding: 0;
                    margin: 0;

                    li {
                        color: #444;
                        padding: 5px 0;
                        border-bottom: 1px dashed #ddd;

                        &:last-child {
                            border-bottom: none;
                        }
                    }

                    .attendee-item {
                        display: flex;
                        justify-content: space-between;
                        align-items: center;

                        .attendee-email {
                            flex: 1;
                        }

                        .attendee-status {
                            padding: 2px 6px;
                            border-radius: 3px;
                            font-weight: 500;

                            &.status-accepted {
                                background: #d4edda;
                                color: #155724;
                            }

                            &.status-declined {
                                background: #f8d7da;
                                color: #721c24;
                            }

                            &.status-tentative {
                                background: #fff3cd;
                                color: #856404;
                            }

                            &.status-needsAction {
                                background: #e7e7e7;
                                color: #495057;
                            }
                        }
                    }
                }

                .no-additional-details {
                    color: #888;
                    font-style: italic;
                    text-align: center;
                    padding: 20px;
                }
            }
        }
    }
}
</style>